
import { motion } from 'framer-motion';
import { ArrowRight, AlertCircle, RotateCcw, Loader2 } from 'lucide-react';
import { GenerationTask } from '../types/generation';
import { useLiveTimer } from '../hooks/useLiveTimer';
import TaskProgressBar from './TaskProgressBar';

interface TaskItemProps {
  task: GenerationTask;
  onCancel: (taskId: string) => void;
  onViewResult: (taskId: string) => void;
  onRetry: (task: GenerationTask) => void;
  isRetrying?: boolean;
}

export function TaskItem({ task, onCancel, onViewResult, onRetry, isRetrying = false }: TaskItemProps) {
  const liveDuration = useLiveTimer(task.startedAt, task.completedAt, task.status === 'pending');


  const formatTime = (timestamp: number) => {
    // Проверяем валидность timestamp
    if (isNaN(timestamp) || timestamp <= 0) {
      return 'Дата недоступна';
    }
    
    try {
      const date = new Date(timestamp);
      
      // Проверяем, что дата валидна
      if (isNaN(date.getTime())) {
        return 'Дата недоступна';
      }
      
      // Добавляем ведущие нули к числам меньше 10
      const padZero = (num: number) => num < 10 ? `0${num}` : num;
      
      const day = padZero(date.getDate());
      const month = padZero(date.getMonth() + 1); // getMonth() возвращает индекс с 0
      const year = date.getFullYear();
      const hours = padZero(date.getHours());
      const minutes = padZero(date.getMinutes());
      
      const result = `${day}.${month}.${year} ${hours}:${minutes}`;
      return result;
    } catch (error) {
      return 'Дата недоступна';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Завершено';
      case 'error':
        return 'Ошибка';
      case 'cancelled':
        return 'Отменено';
      case 'limit-exceeded':
        return 'Лимит превышен';
      default:
        return 'В процессе';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'error':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'limit-exceeded':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 sm:p-4 shadow-sm"
    >
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div className="flex-1 w-full sm:w-auto mb-3 sm:mb-0">
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 mb-2">
          <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(task.status)} w-fit`}>
              {getStatusText(task.status)}
            </span>
            <h3 className="font-medium text-base sm:text-base text-gray-900 dark:text-white">{task.title}</h3>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            <span>{formatTime(task.startedAt)}</span>
            <span className={task.status === 'pending' ? 'font-medium text-blue-600 dark:text-blue-400' : ''}>
              Длительность: {liveDuration}
            </span>
          </div>
          
          {/* Прогресс-бар для задач в процессе выполнения */}
          {task.status === 'pending' && (
            <div className="mt-3">
              <TaskProgressBar 
                taskType={task.type} 
                startTime={task.startedAt}
                progress={task.progress}
                className="w-full"
              />
            </div>
          )}
          
          {(task.status === 'error' || task.status === 'limit-exceeded' || task.error) && (
            <div className="mt-2 text-xs sm:text-sm text-red-600 dark:text-red-400 flex items-start">
              <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 flex-shrink-0 mt-0.5" />
              <span>
                {task.status === 'limit-exceeded' 
                  ? 'Превышен лимит генераций для вашего тарифного плана. Обновите подписку для продолжения работы.'
                  : 'При выполнении задачи произошла ошибка. Перезапустите задачу.'
                }
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto sm:justify-end">
          {task.status === 'pending' && task.canCancel && (
            <button
              onClick={() => onCancel(task.id)}
              className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors font-medium border border-red-200 dark:border-red-800"
            >
              Отменить
            </button>
          )}
          {(task.status === 'error' || task.status === 'cancelled') && (
            <button
              onClick={() => {
                if (!isRetrying) {
                  onRetry(task);
                }
              }}
              disabled={isRetrying}
              className={`flex items-center justify-center w-full sm:w-12 h-12 transition-colors duration-200 rounded-lg ${
                isRetrying 
                  ? 'text-gray-400 cursor-not-allowed bg-gray-100 dark:bg-gray-700' 
                  : 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20'
              }`}
              title={isRetrying ? "Перезапуск в процессе..." : "Перезапустить задачу"}
              style={{ pointerEvents: isRetrying ? 'none' : 'auto' }}
            >
              {isRetrying ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                <RotateCcw className="w-6 h-6" />
              )}
            </button>
          )}
          {task.status === 'limit-exceeded' && (
            <div className="text-xs text-orange-600 dark:text-orange-400 text-center">
              Обновите подписку
            </div>
          )}
          {task.status === 'completed' && (
            <button
              onClick={() => onViewResult(task.id)}
              className="group w-full sm:w-auto flex items-center justify-center px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm bg-transparent sm:bg-gradient-to-r sm:from-blue-500 sm:to-indigo-600 text-blue-600 dark:text-blue-400 sm:text-white rounded-lg shadow-sm sm:shadow-md transform -translate-y-0.5 transition-all duration-200 font-medium hover:shadow-md relative overflow-hidden mt-2 sm:mt-0"
            >
              {/* Градиентная граница для мобильной версии */}
              <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 sm:hidden -z-10" />
              <div className="absolute inset-[1px] rounded-lg bg-white dark:bg-gray-900 sm:hidden -z-10" />
              <span className="relative z-10">Результат</span>
              <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-2 transform group-hover:translate-x-1 transition-transform relative z-10" />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
} 